import type { Dispatch } from 'react';
import { createContext, useContext, useMemo, useReducer } from 'react';

import type { TReducerAction } from './actions';
import { reducer } from './reducer';
import type { TState } from './state';
import { state } from './state';

export const ExportContext = createContext<{
    exportState: TState;
    exportDispatch: Dispatch<TReducerAction>;
}>({
    exportState: state,
    exportDispatch: () => null,
});

export function useExportContext<
    TExportData extends { [key: string]: unknown } = { [key: string]: unknown }
>() {
    type TContextType = {
        exportState: TState<TExportData>;
        exportDispatch: Dispatch<TReducerAction<TExportData>>;
    };
    const contextValue = useContext(
        ExportContext as unknown as React.Context<TContextType>
    );
    return contextValue as TContextType | undefined;
}

interface IExportContextProviderProps<
    TExportData extends { [key: string]: unknown }
> {
    children: React.ReactNode;
    initialState?: TState<TExportData>;
}

export function ExportContextProvider<
    TExportData extends { [key: string]: unknown } = { [key: string]: unknown }
>({ children, initialState }: IExportContextProviderProps<TExportData>) {
    const [exportState, exportDispatch] = useReducer(
        reducer,
        (initialState ?? state) as TState
    );

    return (
        <ExportContext.Provider
            value={useMemo(
                () => ({
                    exportState,
                    exportDispatch,
                }),
                [exportState, exportDispatch]
            )}
        >
            {children}
        </ExportContext.Provider>
    );
}

ExportContextProvider.defaultProps = {
    initialState: state,
};

export * from './state';

export * from './reducer';

export * from './actions';
